import { forwardRef } from 'react';
import { getTheme, isLightColor, type ThemePreset } from '@/lib/themes';
import { cn } from '@/lib/utils';

export interface CardData {
  id: string;
  slug: string;
  display_name: string;
  title?: string | null;
  company?: string | null;
  photo_url?: string | null;
  company_logo_url?: string | null;
  theme_preset?: string | null;
  custom_accent?: string | null;
  custom_bg?: string | null;
  custom_card_bg?: string | null;
  custom_text?: string | null;
  /** Multiplier on the per-context base logo height. 1.0 = no change. */
  logo_scale?: number | null;
}

interface CardProps {
  card: CardData;
  className?: string;
  /** Optional theme override (lets the editor preview without mutating data) */
  themeOverride?: ThemePreset;
  /** Compact mode used in dashboard/list previews */
  compact?: boolean;
}

function initials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((s) => s[0]?.toUpperCase() ?? '')
    .join('');
}

export const Card = forwardRef<HTMLDivElement, CardProps>(function Card(
  { card, className, themeOverride, compact = false },
  ref,
) {
  const theme = themeOverride ?? getTheme(card.theme_preset, card);
  const isLight = isLightColor(theme.cardBg);

  return (
    <div
      ref={ref}
      className={cn(
        'relative isolate flex overflow-hidden rounded-xl',
        'card-inner-glow',
        compact ? 'aspect-[1.6/1]' : 'aspect-[1.586/1]',
        className,
      )}
      style={{
        backgroundColor: theme.cardBg,
        color: theme.text,
      }}
    >
      {/* Photo — full left edge, top to bottom */}
      <div
        className="relative h-full w-[42%] shrink-0"
        style={{ backgroundColor: theme.accent }}
      >
        {card.photo_url ? (
          <img
            src={card.photo_url}
            alt={card.display_name}
            className="h-full w-full object-cover"
            draggable={false}
          />
        ) : (
          <div
            className="flex h-full w-full items-center justify-center text-2xl font-medium"
            style={{ color: theme.cardBg }}
          >
            {initials(card.display_name)}
          </div>
        )}
      </div>

      {/* Info — right side */}
      <div
        className={cn(
          'flex min-w-0 flex-1 flex-col justify-between',
          compact ? 'p-4' : 'p-5',
        )}
      >
        <div className="min-w-0">
          <p
            className={cn(
              'line-clamp-2 font-sans font-semibold leading-[1.05] tracking-tightest',
              compact ? 'text-xl' : 'text-[26px]',
            )}
            style={{ color: theme.text }}
          >
            {card.display_name}
          </p>
          {card.title && (
            <p
              className={cn(
                'mt-1.5 line-clamp-2 font-medium leading-snug opacity-80',
                compact ? 'text-[11px]' : 'text-[13px]',
              )}
            >
              {card.title}
            </p>
          )}
          {card.company && (
            <p className="mt-2 truncate text-[10px] font-semibold uppercase tracking-[0.18em] opacity-65">
              {card.company}
            </p>
          )}
        </div>

        {card.company_logo_url ? (
          (() => {
            const baseH = compact ? 32 : 40;
            const scale =
              typeof card.logo_scale === 'number' && Number.isFinite(card.logo_scale)
                ? Math.min(3, Math.max(0.3, card.logo_scale))
                : 1;
            return (
              <img
                src={card.company_logo_url}
                alt={card.company ?? ''}
                className="object-contain object-left"
                style={{ height: `${Math.round(baseH * scale)}px`, maxWidth: '85%' }}
                draggable={false}
              />
            );
          })()
        ) : (
          <p
            className={cn(
              'font-mono text-[9px] uppercase tracking-widest',
              isLight ? 'opacity-50' : 'opacity-60',
            )}
          >
            haphe.co/{card.slug}
          </p>
        )}
      </div>
    </div>
  );
});
